import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import {toast} from 'react-toastify';
import {NewLineBlock, OrderEta} from 'routes/App/ordersV2/OrdersList/styles';
import {
  DELIVERY_PROVIDER_TYPE,
  ORDER,
  ORDER_SOURCE,
  ORDER_STATUS,
  ORDER_TYPE,
  ORDER_TYPES
} from 'helpers/constants/orders_v2';
import {FlexContainer, FlexItem} from 'components/layout';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import OrderSourceIcon from 'routes/App/ordersV2/components/OrderSourceIcon';
import HoldCountdownPill from 'routes/App/ordersV2/components/HoldCountdownPill';
import {FulfilmentCell} from 'routes/App/ordersV2/components/HoldCountdownPill/styles';
import DriverOptOutCell from 'routes/App/ordersV2/components/HoldCountdownPill/DriverOptOutCell';
import {
  isHybridBookingPending,
  isHybridHoldRunning
} from 'helpers/functionUtils/hybridDelivery';
import StatusButton from 'routes/App/ordersV2/OrdersList/components/StatusButton';
import IconPrint from 'assets/IconPrint';
import {phoneFormatForView} from 'helpers/functionUtils/helpFunctions';
import {
  IconButtonPrinter,
  OrdersTableWrapper
} from 'routes/App/ordersV2/components/OrdersTable/styles';
import {
  checkIsBaDPOS,
  checkIsFullNotToSauceRefundOrder,
  checkIsFullRefundOrder,
  checkIsFutureOrder,
  checkIsRefundOrder,
  getOrderPaymentMethodLabel,
  getOrderIdDisplay,
  hasPickupReadyRequiredFeature,
  isPickupReadyButtonDisabled,
  shouldShowOrderPrice,
  shouldShowPickupReadyUi,
  shouldShowTipsAndTotal
} from 'helpers/functionUtils/ordersUtilsV2';
import IconPickUpDefault from 'assets/icon-pickup-default.svg';
import IconCurbsidePickup from 'assets/icon-curbside-pickup.svg';
import IconDinein from 'assets/icon-dinein.svg';
import IconRestaurantDelivery from 'assets/icon-restaurant-delivery.svg';
import {ButtonPrimary} from 'components/buttons';
import {TableText} from 'components/TableComponent/styles';
import OrdersTableComponent from 'components/OrdersTableComponentV2';

const getFieldsConfig = (
  onStatusChange,
  onOpenDetailsClick,
  onPrintClick,
  isNetworkOnline,
  changeStatusLoading,
  pickupReadyEnabled,
  onPickupReady,
  allowedServiceFeatures,
  showOtterBrandName
) => [
  {
    title: 'ETA',
    // sortable: true,
    onCellClick: onOpenDetailsClick,
    fieldName: 'CookingEndTime.Local',
    renderer: (data) => (
      <OrderEta>
        {checkIsFutureOrder(data) &&
          !checkIsFullNotToSauceRefundOrder(data) && (
            <span className='IconFutureOrder' />
          )}
        {(checkIsFullNotToSauceRefundOrder(data) ||
          (checkIsRefundOrder(data) && !checkIsFutureOrder(data))) && (
          <span className='IconRefundOrder' />
        )}
        {moment(`${data[ORDER.DATE]}Z`).utcOffset(0).format('MMMM D, h:mm A')}
      </OrderEta>
    )
  },
  ...(showOtterBrandName
    ? [
        {
          title: 'Location',
          onCellClick: onOpenDetailsClick,
          renderer: (data) => <div>{data[ORDER.LOCATION]?.storeName || ''}</div>
        }
      ]
    : []),
  {
    title: 'Order ID',
    onCellClick: onOpenDetailsClick,
    fieldName: ORDER.INVOICE_ID,
    renderer: (data) => <div>{getOrderIdDisplay(data)}</div>
  },
  {
    title: 'Customer',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => (
      <FlexContainer className='TableCenter'>
        <NewLineBlock className='wordWrap'>
          {data[ORDER.CUSTOMER]?.fullName || ''}
        </NewLineBlock>
        {data[ORDER.CUSTOMER]?.phone && (
          <NewLineBlock>
            <a
              href={`tel:${data[ORDER.CUSTOMER]?.phone}`}
              alt='customer-phone-number'
              onClick={(e) => e.stopPropagation()}
            >
              {phoneFormatForView(data[ORDER.CUSTOMER]?.phone)}
            </a>
          </NewLineBlock>
        )}
      </FlexContainer>
    )
  },
  {
    title: 'Order Source',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      return (
        <FlexContainer className='align-center justify-center'>
          <OrderSourceIcon order={data} />
        </FlexContainer>
      );
    }
  },
  {
    title: 'Fulfilment',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      return (
        <FlexContainer className='align-center orderProvider'>
          {(() => {
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH
            ) {
              return (
                <FulfilmentCell>
                  <TableText>Sauce delivery</TableText>
                  <HoldCountdownPill
                    order={data}
                    serviceFeatures={allowedServiceFeatures}
                  />
                </FulfilmentCell>
              );
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.LOCATION
            ) {
              return <TableText>Self-delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.UBER
            ) {
              return <TableText>Uber delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.DOORDASH
            ) {
              return <TableText>DoorDash delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.GRUBHUB
            ) {
              return <TableText>GrubHub delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.SLICE
            ) {
              return <TableText>Slice delivery</TableText>;
            }
            if (
              data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
              data[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
              data[ORDER.SOURCE] === ORDER_SOURCE.EZCATER
            ) {
              return <TableText>EZCater delivery</TableText>;
            }
            if (data[ORDER.ORDER_TYPE] === ORDER_TYPE.DINE_IN) {
              return <TableText>Dine in</TableText>;
            }
            return <TableText>Pickup</TableText>;
          })()}
        </FlexContainer>
      );
    }
  },
  {
    title: 'Amount',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      if (!shouldShowTipsAndTotal(data, allowedServiceFeatures)) {
        return <div />;
      }

      const amount = data[ORDER.TOTALS]?.locationTotal;
      const paymentMethod = getOrderPaymentMethodLabel(
        data[ORDER.PAYMENT_METHOD]
      );
      const amountLabel = shouldShowOrderPrice(amount, data[ORDER.SOURCE])
        ? `$${groupDigital(Number(amount || 0).toFixed(2))}`
        : '';

      return (
        <div>{[amountLabel, paymentMethod].filter(Boolean).join(' ')}</div>
      );
    }
  },
  {
    title: 'Status',
    fieldName: 'OrderStatus',
    renderer: (data) => {
      return (
        <div
          style={
            !isNetworkOnline ||
            changeStatusLoading === data[ORDER.ID] ||
            data[ORDER.ORDER_STATUS] === ORDER_STATUS.CANCELLED
              ? {opacity: '0.5', pointerEvents: 'none'}
              : null
          }
        >
          <StatusButton
            order={data}
            onStatusChange={(status) => {
              onStatusChange(
                data[ORDER.ID],
                status,
                data[ORDER.ORDER_STATUS],
                data[ORDER.DELIVERY]?.isDispatch,
                data[ORDER.SOURCE]
              );
            }}
            pickupReadyEnabled={pickupReadyEnabled}
            serviceFeatures={allowedServiceFeatures}
          />
        </div>
      );
    }
  },
  {
    renderer: (data) => {
      if (
        isHybridHoldRunning(data, allowedServiceFeatures) ||
        isHybridBookingPending(data, allowedServiceFeatures)
      ) {
        return (
          <DriverOptOutCell
            order={data}
            serviceFeatures={allowedServiceFeatures}
          />
        );
      }

      return shouldShowPickupReadyUi(
        data,
        pickupReadyEnabled,
        allowedServiceFeatures
      ) ? (
        <FlexContainer className='justify-end'>
          <ButtonPrimary
            className={cn('ButtonSmall', {
              disabledWithClick: isPickupReadyButtonDisabled(
                data,
                pickupReadyEnabled,
                allowedServiceFeatures
              )
            })}
            onClick={() => {
              if (
                isPickupReadyButtonDisabled(
                  data,
                  pickupReadyEnabled,
                  allowedServiceFeatures
                )
              ) {
                toast.error(
                  'Cannot do pickup ready. Expect the courier within pickup ETA.'
                );
              } else {
                onPickupReady(
                  data[ORDER.ID],
                  ORDER_STATUS.READY,
                  data[ORDER.ORDER_STATUS]
                );
              }
            }}
          >
            <span>Ready</span>
          </ButtonPrimary>
        </FlexContainer>
      ) : null;
    }
  },
  {
    renderer: (item) => (
      <FlexContainer className='justify-end'>
        <IconButtonPrinter
          data-gtm='printButton'
          onClick={() => onPrintClick(item)}
        >
          <IconPrint data-gtm='printButton' />
        </IconButtonPrinter>
      </FlexContainer>
    )
  }
];

const OrdersTable = ({
  onStatusChange,
  openDetails,
  printOrder,
  orders,
  loading,
  onSort,
  orderBy,
  orderDirection,
  isNetworkOnline,
  changeStatusLoading,
  isOrdersTable,
  pickupReadyEnabled,
  onPickupReady,
  allowedServiceFeatures,
  showOtterBrandName
}) => {
  return (
    <OrdersTableWrapper
      className={cn('OrdersTableWrapper', {
        NoOrders: !orders.length && isOrdersTable,
        withPickupReady:
          pickupReadyEnabled ||
          hasPickupReadyRequiredFeature(allowedServiceFeatures),
        withOtterBrandName: showOtterBrandName
      })}
    >
      <OrdersTableComponent
        fields={getFieldsConfig(
          onStatusChange,
          openDetails,
          printOrder,
          isNetworkOnline,
          changeStatusLoading,
          pickupReadyEnabled,
          onPickupReady,
          allowedServiceFeatures,
          showOtterBrandName
        )}
        data={orders}
        loading={loading}
        rowKeyPath={ORDER.ID}
        onSort={onSort}
        orderBy={orderBy}
        orderDirection={orderDirection}
        pickupReadyEnabled={
          pickupReadyEnabled ||
          hasPickupReadyRequiredFeature(allowedServiceFeatures)
        }
      />
    </OrdersTableWrapper>
  );
};

OrdersTable.propTypes = {
  onStatusChange: PropTypes.func.isRequired,
  openDetails: PropTypes.func.isRequired,
  printOrder: PropTypes.func.isRequired,
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isNetworkOnline: PropTypes.bool.isRequired,
  onSort: PropTypes.func,
  orderBy: PropTypes.string,
  orderDirection: PropTypes.string,
  changeStatusLoading: PropTypes.string.isRequired,
  isOrdersTable: PropTypes.bool,
  pickupReadyEnabled: PropTypes.bool,
  onPickupReady: PropTypes.func.isRequired,
  allowedServiceFeatures: PropTypes.array,
  showOtterBrandName: PropTypes.bool
};

OrdersTable.defaultProps = {
  onSort: () => {},
  orderBy: '',
  orderDirection: '',
  isOrdersTable: false,
  pickupReadyEnabled: false,
  allowedServiceFeatures: [],
  showOtterBrandName: false
};

export default OrdersTable;
